import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  BookOpen,
  UserCheck,
  FileBarChart,
  Settings,
  LogOut,
  Menu,
  GraduationCap,
  PanelLeft,
  PanelRight
} from 'lucide-react';

import { Button } from "../../components/ui/button";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "../../components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { cn } from "../../lib/utils";

interface SidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ activeSection, onSectionChange, isCollapsed, onToggleCollapse }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const menuItems = [
    {
      id: 'dashboard',
      name: 'Tableau de Bord',
      icon: <LayoutDashboard className="w-5 h-5" />,
    },
    {
      id: 'publications',
      name: 'Publications',
      icon: <FileText className="w-5 h-5" />,
      // badge: '3'
    },
    {
      id: 'students',
      name: 'Étudiants',
      icon: <GraduationCap className="w-5 h-5" />,
      // badge: '156'
    },
    {
      id: 'courses',
      name: 'Cours',
      icon: <BookOpen className="w-5 h-5" />,
      // badge: '48'
    },
    {
      id: 'professors',
      name: 'Professeurs',
      icon: <UserCheck className="w-5 h-5" />,
      // badge: '24'
    },
    {
      id: 'reports',
      name: 'Rapports',
      icon: <FileBarChart className="w-5 h-5" />,
    },
    {
      id: 'settings',
      name: 'Paramètres',
      icon: <Settings className="w-5 h-5" />,
    }
  ].filter(item => {
    if (item.id === 'settings') {
      return user?.role === 'SUPER_ADMIN';
    }
    return true;
  });

  const handleLogout = () => {
    navigate('/');
    setTimeout(() => {
      logout();
    }, 100);
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className={cn("p-6 flex items-center", isCollapsed ? "justify-center" : "justify-between")}>
        {!isCollapsed && (
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-green-600 rounded-lg flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-lg">I</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">ISTMECH</h1>
              <p className="text-xs text-muted-foreground">Admin Panel</p>
            </div>
          </div>
        )}

        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          className="hidden lg:flex"
        >
          {isCollapsed ? <PanelLeft className="h-5 w-5" /> : <PanelRight className="h-5 w-5" />}
        </Button>
      </div>

      <ScrollArea className="flex-1 px-4">
        <nav className="space-y-2">
          {menuItems.map((item) => (
            <Button
              key={item.id}
              variant={activeSection === item.id ? "default" : "ghost"}
              className={cn(
                "w-full justify-start",
                activeSection === item.id ? 'bg-blue-600 hover:bg-blue-700 text-white' : '',
                isCollapsed && "justify-center px-2"
              )}
              onClick={() => onSectionChange(item.id)}
              title={isCollapsed ? item.name : undefined}
            >
              {item.icon}
              {!isCollapsed && <span className="ml-3 flex-1 text-left">{item.name}</span>}
              {/* {!isCollapsed && item.badge && (
                <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${activeSection === item.id
                  ? 'bg-white/20 text-white'
                  : 'bg-muted text-muted-foreground'
                  }`}>
                  {item.badge}
                </span>
              )} */}
            </Button>
          ))}
        </nav>
      </ScrollArea>

      <div className="p-4 mt-auto border-t">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className={cn("w-full justify-start px-2 hover:bg-muted", isCollapsed && "justify-center")}>
              <Avatar className="h-8 w-8">
                <AvatarImage src={`https://ui-avatars.com/api/?name=${user?.name}&background=random`} />
                <AvatarFallback>{user?.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              {!isCollapsed && (
                <div className="flex flex-col items-start overflow-hidden ml-3">
                  <span className="text-sm font-medium truncate w-full text-left">{user?.name}</span>
                  <span className="text-xs text-muted-foreground truncate w-full text-left">{user?.email}</span>
                </div>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" forceMount side={isCollapsed ? "right" : "top"}>
            <DropdownMenuLabel>Mon Compte</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/profile')}>
              <UserCheck className="mr-2 h-4 w-4" />
              <span>Profil</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              <span>Déconnexion</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Sidebar (Sheet) */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" className="shadow-md">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72">
            <SidebarContent />
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop Sidebar */}
      <div className={cn(
        "hidden lg:flex flex-col fixed inset-y-0 border-r bg-background z-40 transition-all duration-300",
        isCollapsed ? "w-20" : "w-72"
      )}>
        <SidebarContent />
      </div>
    </>
  );
};

export default Sidebar;
